/**
 * Auditor Application Review Page
 *
 * Detailed review interface for auditors showing:
 * - Application and company information
 * - Document verification
 * - Pre-audit report from consultant
 * - Readiness assessment
 * - Actions (schedule audit, approve, reject, request info)
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Mail, Calendar, FileText, Check, Search, XCircle } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { ProgressBar } from '../../components/common/ProgressBar';
import { formatDate } from '../../utils/formatting';
import { mockApi } from '../../services/mockApi';
import { PermissionWrapper } from '../../components/auth';
import { UserPermission } from '../../types/user';
import type { Application } from '../../types/application';

// Mock ID fallback
const MOCK_APPLICATION_ID = 'app-001';

export const AuditorApplicationReviewPage: React.FC = () => {
  const { t } = useTranslation(['common', 'auditor', 'company']);
  const { id } = useParams<{ id: string }>();
  const applicationId = id || MOCK_APPLICATION_ID;

  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState<Application | null>(null);

  useEffect(() => {
    loadApplicationDetails();
  }, [applicationId]);

  const loadApplicationDetails = async () => {
    setLoading(true);
    try {
      const response = await mockApi.getApplication(applicationId);
      if (response.success && response.data) {
        setApplication(response.data);
      }
    } catch (error) {
      console.error('Error loading application:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleAudit = () => {
    alert(`Schedule audit for ${application?.companyName}`);
    // Navigate to audit scheduling
  };

  const handleApproveForAudit = () => {
    alert(`Approve ${application?.companyName} for official audit`);
  };

  const handleRejectApplication = () => {
    const reason = prompt('Please provide rejection reason:');
    if (reason) {
      alert(`Application rejected: ${reason}`);
    }
  };

  const handleRequestMoreInfo = () => {
    alert(`Request more information from ${application?.companyName}`);
  };

  const handleVerifyDocument = (docId: string) => {
    alert(`Verify document: ${docId}`);
  };

  const getStatusVariant = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'AUDIT_SCHEDULED':
      case 'AUDIT_IN_PROGRESS':
        return 'warning';
      case 'REJECTED':
        return 'error';
      case 'UNDER_REVIEW':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <PageLayout
        pageTitle={t('auditor:review.title')}
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('auditor:dashboard.title'), href: '/auditor/dashboard' },
          { label: t('auditor:applications.applicationQueue'), href: '/auditor/applications' },
          { label: t('auditor:review.title') },
        ]}
      >
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">{t('common:app.loading')}</p>
        </div>
      </PageLayout>
    );
  }

  if (!application) {
    return (
      <PageLayout
        pageTitle={t('auditor:review.title')}
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('auditor:dashboard.title'), href: '/auditor/dashboard' },
          { label: t('auditor:applications.applicationQueue'), href: '/auditor/applications' },
          { label: t('auditor:review.title') },
        ]}
      >
        <div className="text-center py-12">
          <p className="text-gray-500">Application not found</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={`Review: ${application.companyName}`}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('auditor:dashboard.title'), href: '/auditor/dashboard' },
        { label: t('auditor:applications.applicationQueue'), href: '/auditor/applications' },
        { label: application.companyName },
      ]}
      actions={
        <div className="flex gap-2">
          <PermissionWrapper permission={UserPermission.REVIEW_APPLICATION}>
            <Button variant="outline" onClick={handleRequestMoreInfo} icon={<Mail className="w-4 h-4" />}>
              {t('auditor:actions.requestInfo')}
            </Button>
          </PermissionWrapper>
          <PermissionWrapper permission={UserPermission.SCHEDULE_AUDIT}>
            <Button variant="outline" onClick={handleScheduleAudit} icon={<Calendar className="w-4 h-4" />}>
              {t('auditor:actions.scheduleAudit')}
            </Button>
          </PermissionWrapper>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left/Center Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Application Overview */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">{t('auditor:review.applicationInfo')}</h3>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Application Type</p>
                  <p className="mt-1">{application.type}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <div className="mt-1">
                    <Badge variant={getStatusVariant(application.status)}>
                      {application.status.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Submitted Date</p>
                  <p className="mt-1">{formatDate(application.submittedAt || application.createdAt)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Last Updated</p>
                  <p className="mt-1">{formatDate(application.updatedAt)}</p>
                </div>
              </div>

              {/* Completeness Progress */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-500">{t('auditor:review.completenessCheck')}</p>
                  <p className="text-sm text-gray-700">{application.completenessScore}%</p>
                </div>
                <ProgressBar value={application.completenessScore} variant="default" />
              </div>
            </CardBody>
          </Card>

          {/* Company Information */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">{t('auditor:review.companyInfo')}</h3>
            </CardHeader>
            <CardBody>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                  <dd className="mt-1 text-sm">{application.companyName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Applicant Name</dt>
                  <dd className="mt-1 text-sm">{application.applicantName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Contact</dt>
                  <dd className="mt-1 text-sm">
                    <span className="text-gray-700">View in system</span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Application ID</dt>
                  <dd className="mt-1 text-sm font-mono">{application.id}</dd>
                </div>
              </dl>
            </CardBody>
          </Card>

          {/* Documents */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{t('auditor:review.documents')}</h3>
                <Badge variant="info">{application.documents?.length || 0} documents</Badge>
              </div>
            </CardHeader>
            <CardBody>
              <div className="space-y-3">
                {application.documents && application.documents.length > 0 ? (
                  application.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-6 h-6 text-neutral-600" />
                        <div>
                          <p className="font-medium text-gray-800">{doc.name}</p>
                          <p className="text-sm text-gray-500">{doc.type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={doc.verified ? 'success' : 'warning'}>
                          {doc.verified ? t('auditor:review.verified') : t('auditor:review.pending')}
                        </Badge>
                        {!doc.verified && (
                          <PermissionWrapper permission={UserPermission.REVIEW_APPLICATION}>
                            <Button variant="outline" size="sm" onClick={() => handleVerifyDocument(doc.id)}>
                              {t('auditor:actions.verifyDocument')}
                            </Button>
                          </PermissionWrapper>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No documents uploaded</p>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Pre-Audit Report */}
          {application.status !== 'SUBMITTED' && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">{t('auditor:review.preAuditReport')}</h3>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('auditor:review.readinessAssessment')}</p>
                    <div className="mt-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <ProgressBar
                            value={application.completenessScore}
                            variant={
                              application.completenessScore >= 80 ? 'success' :
                              application.completenessScore >= 60 ? 'warning' : 'error'
                            }
                          />
                        </div>
                        <span className="text-sm font-medium">{application.completenessScore}%</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('auditor:review.consultantFindings')}</p>
                    <p className="mt-1 text-sm text-gray-700">
                      Pre-audit checklist completed with {application.completenessScore >= 80 ? 'minimal' : 'some'} findings.
                      Company shows {application.completenessScore >= 80 ? 'good' : 'moderate'} readiness for official audit.
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </div>

        {/* Sidebar - Right Column */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Actions</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-2">
                {application.status === 'UNDER_REVIEW' && (
                  <>
                    <PermissionWrapper permission={UserPermission.APPROVE_APPLICATION}>
                      <Button fullWidth onClick={handleApproveForAudit} icon={<Check className="w-4 h-4" />}>
                        {t('auditor:review.approveForAudit')}
                      </Button>
                    </PermissionWrapper>
                    <PermissionWrapper permission={UserPermission.SCHEDULE_AUDIT}>
                      <Button fullWidth onClick={handleScheduleAudit} icon={<Calendar className="w-4 h-4" />}>
                        {t('auditor:review.scheduleAudit')}
                      </Button>
                    </PermissionWrapper>
                  </>
                )}
                {application.status === 'AUDIT_SCHEDULED' && (
                  <PermissionWrapper permission={UserPermission.CONDUCT_AUDIT}>
                    <Button fullWidth onClick={() => window.location.href = `/auditor/audits/${applicationId}`} icon={<Search className="w-4 h-4" />}>
                      {t('auditor:actions.conductAudit')}
                    </Button>
                  </PermissionWrapper>
                )}
                <PermissionWrapper permission={UserPermission.REVIEW_APPLICATION}>
                  <Button variant="outline" fullWidth onClick={handleRequestMoreInfo} icon={<Mail className="w-4 h-4" />}>
                    {t('auditor:review.requestMoreInfo')}
                  </Button>
                </PermissionWrapper>
                <PermissionWrapper permission={UserPermission.CONDUCT_AUDIT}>
                  <Button variant="outline" fullWidth onClick={() => window.location.href = `/auditor/ncr/create?app=${applicationId}`} icon={<FileText className="w-4 h-4" />}>
                    {t('auditor:actions.createNCR')}
                  </Button>
                </PermissionWrapper>
                <PermissionWrapper permission={UserPermission.APPROVE_APPLICATION}>
                  <Button variant="outline" fullWidth onClick={handleRejectApplication} icon={<XCircle className="w-4 h-4" />}>
                    {t('auditor:review.rejectApplication')}
                  </Button>
                </PermissionWrapper>
              </div>
            </CardBody>
          </Card>

          {/* Readiness Status */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">{t('auditor:review.readinessAssessment')}</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Completeness</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <ProgressBar
                        value={application.completenessScore}
                        variant={
                          application.completenessScore >= 80 ? 'success' :
                          application.completenessScore >= 60 ? 'warning' : 'error'
                        }
                      />
                    </div>
                    <span className="text-sm font-medium">{application.completenessScore}%</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Document Verification</p>
                  <Badge variant={
                    application.documents?.every(d => d.verified) ? 'success' : 'warning'
                  }>
                    {application.documents?.filter(d => d.verified).length || 0} / {application.documents?.length || 0} Verified
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Readiness Level</p>
                  <Badge variant={
                    application.completenessScore >= 80 ? 'success' :
                    application.completenessScore >= 60 ? 'warning' : 'error'
                  }>
                    {application.completenessScore >= 80 ? 'Ready' :
                     application.completenessScore >= 60 ? 'Needs Improvement' : 'Not Ready'}
                  </Badge>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Review Notes */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">{t('auditor:review.reviewNotes')}</h3>
            </CardHeader>
            <CardBody>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={4}
                placeholder={t('auditor:review.addNote')}
              />
              <Button variant="outline" size="sm" fullWidth className="mt-2">
                {t('auditor:review.addNote')}
              </Button>
            </CardBody>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

